/**
 * Warehouse Storage API Client
 *
 * Frontend API client for managing warehouse-storage account mappings.
 * Part of Phase 8: Storage Mapping extension for warehouses.
 */

import { apiClient } from '../../../services/apiClient';

// Types matching backend
export type StorageAccountPurpose = 'primary' | 'secondary' | 'archive' | 'overflow';
export type StorageMappingStatus = 'active' | 'inactive' | 'pending_removal';

export interface WarehouseStorageMapping {
  id: string;
  warehouseId: string;
  storageAccountId: string;
  purpose: StorageAccountPurpose;
  isPrimary: boolean;
  status: StorageMappingStatus;
  createdAt: string;
  updatedAt: string;
  // Extended storage account info (from joined query)
  storageAccountName?: string;
  storageAccountType?: string;
  containerName?: string;
  region?: string;
  // Backup statistics
  backupCount?: number;
  totalStorageBytes?: number;
  lastBackupAt?: string | null;
}

export interface StorageAccountDetails {
  id: string;
  name: string;
  type: string;
  containerName: string;
  region: string;
  isActive: boolean;
}

export interface AddStorageAccountInput {
  storageAccountId: string;
  purpose?: StorageAccountPurpose;
  isPrimary?: boolean;
}

export interface UpdateStorageMappingInput {
  purpose?: StorageAccountPurpose;
  isPrimary?: boolean;
  status?: StorageMappingStatus;
}

export interface ActiveBackupCheck {
  hasActiveBackups: boolean;
  backupCount: number;
  lastBackupAt: string | null;
  oldestBackupAt: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface ApiMessageResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Get all storage accounts mapped to a warehouse
 */
export async function getStorageAccountsForWarehouse(
  warehouseId: string
): Promise<WarehouseStorageMapping[]> {
  const response = await apiClient.get<ApiResponse<WarehouseStorageMapping[]>>(
    `/api/v1/warehouses/${warehouseId}/storage-accounts`
  );
  return response.data.data;
}

/**
 * Get the primary storage account for a warehouse
 */
export async function getPrimaryStorageAccount(
  warehouseId: string
): Promise<WarehouseStorageMapping | null> {
  try {
    const response = await apiClient.get<ApiResponse<WarehouseStorageMapping>>(
      `/api/v1/warehouses/${warehouseId}/storage-accounts/primary`
    );
    return response.data.data;
  } catch (error: unknown) {
    // If 404, return null (no storage account found)
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Add a storage account to a warehouse
 */
export async function addStorageAccount(
  warehouseId: string,
  input: AddStorageAccountInput
): Promise<WarehouseStorageMapping> {
  const response = await apiClient.post<ApiResponse<WarehouseStorageMapping>>(
    `/api/v1/warehouses/${warehouseId}/storage-accounts`,
    input
  );
  return response.data.data;
}

/**
 * Update a storage account mapping
 */
export async function updateStorageMapping(
  warehouseId: string,
  mappingId: string,
  input: UpdateStorageMappingInput
): Promise<WarehouseStorageMapping> {
  const response = await apiClient.put<ApiResponse<WarehouseStorageMapping>>(
    `/api/v1/warehouses/${warehouseId}/storage-accounts/${mappingId}`,
    input
  );
  return response.data.data;
}

/**
 * Remove a storage account from a warehouse
 *
 * @param warehouseId - The warehouse ID
 * @param mappingId - The mapping ID to remove
 * @param force - Force removal even if backups exist (will delete backup records)
 */
export async function removeStorageAccount(
  warehouseId: string,
  mappingId: string,
  force: boolean = false
): Promise<{ message: string }> {
  const response = await apiClient.delete<ApiMessageResponse>(
    `/api/v1/warehouses/${warehouseId}/storage-accounts/${mappingId}`,
    { params: { force: force ? 'true' : undefined } }
  );
  return { message: response.data.message };
}

/**
 * Set a storage account as the primary for a warehouse
 */
export async function setAsPrimary(
  warehouseId: string,
  mappingId: string
): Promise<WarehouseStorageMapping> {
  return updateStorageMapping(warehouseId, mappingId, { isPrimary: true });
}

/**
 * Change the purpose of a storage account mapping
 */
export async function changePurpose(
  warehouseId: string,
  mappingId: string,
  purpose: StorageAccountPurpose
): Promise<WarehouseStorageMapping> {
  return updateStorageMapping(warehouseId, mappingId, { purpose });
}

/**
 * Deactivate a storage account mapping (mark as inactive)
 */
export async function deactivateMapping(
  warehouseId: string,
  mappingId: string
): Promise<WarehouseStorageMapping> {
  return updateStorageMapping(warehouseId, mappingId, { status: 'inactive' });
}

/**
 * Reactivate a storage account mapping
 */
export async function reactivateMapping(
  warehouseId: string,
  mappingId: string
): Promise<WarehouseStorageMapping> {
  return updateStorageMapping(warehouseId, mappingId, { status: 'active' });
}

// Convenience type for component props
export type WarehouseStorageApi = {
  getStorageAccountsForWarehouse: typeof getStorageAccountsForWarehouse;
  getPrimaryStorageAccount: typeof getPrimaryStorageAccount;
  addStorageAccount: typeof addStorageAccount;
  updateStorageMapping: typeof updateStorageMapping;
  removeStorageAccount: typeof removeStorageAccount;
  setAsPrimary: typeof setAsPrimary;
  changePurpose: typeof changePurpose;
  deactivateMapping: typeof deactivateMapping;
  reactivateMapping: typeof reactivateMapping;
};

export const warehouseStorageApi: WarehouseStorageApi = {
  getStorageAccountsForWarehouse,
  getPrimaryStorageAccount,
  addStorageAccount,
  updateStorageMapping,
  removeStorageAccount,
  setAsPrimary,
  changePurpose,
  deactivateMapping,
  reactivateMapping,
};

export default warehouseStorageApi;
